import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptor,
  HttpHandler,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SecurityService } from '../../services/security.service';

/**
 * RagAuthInterceptor — headers de sesión SOLO para el proxy RAG del security service.
 *
 * Reglas:
 *  • Solo intercepta requests a `${backendUrl}/rag`.
 *  • Con sesión: `Authorization: Bearer <jwt>` + `X-Partner-ID` (partner efectivo,
 *    incluido el elegido en el combo de superAdmin). Sin sesión: modo anónimo.
 *  • `X-Skip-Interceptor` para que la cadena principal no reprocese.
 *  • El partner lo VALIDA el proxy contra el JWT firmado; aquí solo se envía.
 */
@Injectable()
export class RagAuthInterceptor implements HttpInterceptor {

  private readonly ragBaseUrl = `${environment.backendUrl}/rag`;

  constructor(private securityService: SecurityService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.startsWith(this.ragBaseUrl)) {
      return next.handle(req);
    }

    const token = this.securityService.getToken();
    const request = req.clone({ headers: this.buildRagHeaders(req.headers, token) });

    return next.handle(request).pipe(
      catchError(error => this.retryOnceAfterUnauthorized(error, req, next, token))
    );
  }

  private buildRagHeaders(headers: HttpHeaders, token: string | null): HttpHeaders {
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
      const partner = this.securityService.getTokenPartner();
      if (partner !== null && partner !== undefined) {
        headers = headers.set('X-Partner-ID', String(partner));
      } else {
        headers = headers.delete('X-Partner-ID');
      }
    } else {
      headers = headers.delete('Authorization').delete('X-Partner-ID');
    }

    return headers.set('X-Skip-Interceptor', 'true');
  }

  private retryOnceAfterUnauthorized(
    error: unknown,
    req: HttpRequest<any>,
    next: HttpHandler,
    originalToken: string | null
  ): Observable<HttpEvent<any>> {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !originalToken) {
      return throwError(() => error);
    }

    return this.securityService.refreshToken().pipe(
      switchMap(response => {
        const refreshedToken = response?.jwt;
        if (!refreshedToken) {
          return throwError(() => error);
        }
        const retried = req.clone({ headers: this.buildRagHeaders(req.headers, refreshedToken) });
        return next.handle(retried);
      }),
      catchError(() => throwError(() => error))
    );
  }
}
